import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  OverlayView,
} from "@react-google-maps/api";
import axios from "axios";
import { styled } from "@mui/material/styles";
import { Box, CircularProgress } from "@mui/material";
import BusIcon from "../../images/vehicle/Map_Vehicle_active.svg";
import DeadBusIcon from "../../images/vehicle/Map_Vehicle_dead.svg";
import InactiveBusIcon from "../../images/vehicle/Map_Vehicle_inactive.svg";
import HighlightedBusIconDestination from "../../images/vehicle/NavigationIcon.svg";
import HighlightedBusIconSource from "../../images/vehicle/NavigationIcon.svg";
import config from "../../config.js";
import useSSE from "../../hooks/useSSE.js";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { uberMapOptions, MAP_ORIENTATION } from "./mapOptions.js";
import {
  calculateRealisticETA,
  calculateImprovedDistance,
  formatTime,
  calculateDistance,
  normalizeDeviceData,
  detectMovement,
  determineVehicleDirection,
  calculateLabelOffsets,
  calculateBearing,
  calculateMapRotation,
  getOverallRouteBearing,
} from "./commonFunctions.js";
import {
  POSITION_HISTORY_SIZE,
  MOVEMENT_THRESHOLD,
  TERMINAL_THRESHOLD,
  ROUTE_PROXIMITY_THRESHOLD,
  CONSTANTS,
  colors,
} from "./mapOptions";
import {
  theme,
  MapContainer,
  MapWrapper,
  StopLine,
  StopLabel,
  VehicleLabel,
  VehicleLine,
  VehicleIconContainer,
  InfoBox,
  AnimatedCircle,
  InfoText,
  SubInfoText,
} from "./MapStyles";
import {
  getRouteById,
  fetchStopsForRoute,
  fetchStopDetails,
  fetchInitialData,
} from "./DriverConsoleAPI.js";
import { BorderAll } from "@mui/icons-material";
import SourceStopIcon from "../../images/driverConsole/bxs-map.svg.png";
import DestinationStopIcon from "../../images/driverConsole/bxs-flag.svg.svg";
import RegularStopIcon from "../../images/driverConsole/regularMarker.svg";
import NextStopIcon from "../../images/driverConsole/nextStopMarker.svg";
const GOOGLE_MAPS_LIBRARIES = ['geometry'];

const center = { lat: 13.1986, lng: 77.7066 };

// Update the component props to include onPositionUpdate
function DriverSpecificMap({ onNextStopUpdate, onPositionUpdate, routeId, orgId, vehicleId, isOverSpeeding }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyD7rqUpTzUpEbxm-Xc7ikltFJGzOhd92Qk",
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: "weekly",
  });
  const [map, setMap] = useState(null);
  const [data, setData] = useState({ vehicles: [], routes: [], stops: [] });
  const [directions, setDirections] = useState([]);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [vehicleDirections, setVehicleDirections] = useState({});
  const [loadingStates, setLoadingStates] = useState({
    routes: true,
    stops: true,
    directions: true,
  });
  const [visitedStops, setVisitedStops] = useState(new Set());
  const [stopVisitTimer, setStopVisitTimer] = useState(null);
  const [lastVisitedStop, setLastVisitedStop] = useState(null);
  const [lastStopDistance, setLastStopDistance] = useState(null);
  const [stopDetails, setStopDetails] = useState({});
  const [zoomLevel, setZoomLevel] = useState(
    MAP_ORIENTATION.DEFAULT_ZOOM || 15
  );
  const [mapRotation, setMapRotation] = useState(0);
  console.log(mapRotation)
  const lastPositionRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const [stopRotation, setStopRotation] = useState(360);


  // const labelRotations = () => {
  //   // Update rotation by adding 90 degrees
  //   setStopRotation(360);
  // };

  const effectiveRouteId =
    routeId || new URLSearchParams(location.search).get("routeId") || "55";
  const effectiveOrgId =
    orgId || new URLSearchParams(location.search).get("organizationId");
  const effectiveVehicleId =
    vehicleId || new URLSearchParams(location.search).get("vehicleId");

  const getDeviceId = useCallback(
    (vehicleId) => {
      const vehicle = data.vehicles.find(
        (v) => v.id === parseInt(vehicleId) || v.device_id === vehicleId
      );
      return vehicle ? vehicle.device_id : vehicleId;
    },
    [data.vehicles]
  );

  const effectiveDeviceId = useMemo(
    () => getDeviceId(effectiveVehicleId),
    [effectiveVehicleId, getDeviceId]
  );

  useEffect(() => {
    if (effectiveRouteId && effectiveOrgId && effectiveVehicleId) {
      console.log("Fetching initial data with:", {
        effectiveRouteId,
        effectiveOrgId,
        effectiveVehicleId,
        effectiveDeviceId,
      });
      fetchInitialData(
        effectiveRouteId,
        effectiveOrgId,
        effectiveDeviceId,
        setData,
        setLoadingStates,
        setVehicleIds,
        setStopDetails,
        navigate
      ).then(() => {
        console.log("Initial data fetched:", data);
      }).catch((error) => {
        console.error("Error fetching initial data:", error);
      });
    } else {
      console.error("Missing routeId, organizationId, or vehicleId", {
        effectiveRouteId,
        effectiveOrgId,
        effectiveVehicleId,
      });
      navigate("/");
    }
  }, [
    effectiveRouteId,
    effectiveOrgId,
    effectiveVehicleId,
    effectiveDeviceId,
    navigate,
  ]);

  useEffect(() => {
    if (!data.stops.length || !effectiveDeviceId) {
      console.log("Skipping stop details fetch due to missing data:", {
        stops: data.stops.length,
        effectiveDeviceId,
      });
      return;
    }
    const updateStopDetails = async () => {
      console.log("Fetching stop details for stops:", data.stops);
      const details = {};
      for (let i = 0; i < data.stops.length; i++) {
        const stop = data.stops[i];
        try {
          details[stop.id] = await fetchStopDetails(
            stop,
            i,
            data.stops.length,
            effectiveDeviceId
          );
          console.log(`Stop details for stop ${stop.id}:`, details[stop.id]);
        } catch (error) {
          console.error(`Error fetching stop details for stop ${stop.id}:`, error);
        }
      }
      setStopDetails((prev) => {
        console.log("Updated stop details:", { ...prev, ...details });
        return { ...prev, ...details };
      });
    };
    updateStopDetails();
    const intervalId = setInterval(updateStopDetails, 80000);
    return () => clearInterval(intervalId);
  }, [data.stops, effectiveDeviceId]);

  useEffect(() => {
    if (isLoaded && data.routes.length > 0 && map) {
      setLoadingStates((prev) => ({ ...prev, directions: true }));
      const directionsService = new window.google.maps.DirectionsService();
      const fetchDirections = async () => {
        const directionsPromises = data.routes.map((route) => {
          return new Promise((resolve, reject) => {
            const waypoints = route.routeCoordinates
              .slice(1, -1)
              .map((coord) => ({
                location: new window.google.maps.LatLng(
                  parseFloat(coord.latitude),
                  parseFloat(coord.longitude)
                ),
                stopover: coord.isStop || false,
              }));

            const origin = new window.google.maps.LatLng(
              parseFloat(route.routeCoordinates[0].latitude),
              parseFloat(route.routeCoordinates[0].longitude)
            );
            const destination = new window.google.maps.LatLng(
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .latitude
              ),
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .longitude
              )
            );

            directionsService.route(
              {
                origin,
                destination,
                waypoints,
                optimizeWaypoints: false,
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  resolve({ ...result, routeColor: route.routeColor });
                } else {
                  console.error(
                    `Error fetching directions for route ${route.id}: ${status}`
                  );
                  reject(`Error fetching directions: ${status}`);
                }
              }
            );
          });
        });

        try {
          const results = await Promise.all(directionsPromises);
          setDirections(results);
          console.log("Directions set:", results);
        } catch (error) {
          console.error("Error fetching directions:", error);
        } finally {
          setLoadingStates((prev) => ({ ...prev, directions: false }));
        }
      };
      fetchDirections();
    }
  }, [isLoaded, data.routes, map]);

  const zoomOffset = Math.max(
    0,
    (zoomLevel - (MAP_ORIENTATION.DEFAULT_ZOOM || 15)) * 5
  );

  const findRouteIndex = (position) => {
    if (!position || !data.routes[0]?.routeCoordinates?.length) return -1;
    let minDistance = Infinity;
    let closestIndex = -1;
    data.routes[0].routeCoordinates.forEach((coord, index) => {
      const distance = calculateImprovedDistance(position, {
        lat: parseFloat(coord.latitude),
        lng: parseFloat(coord.longitude),
      });
      if (distance !== null && distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  };

  const getBestMetrics = (
    calculatedDistance,
    backendDistance,
    calculatedETA,
    backendETA
  ) => {
    let finalDistance = calculatedDistance;
    let finalETA = calculatedETA;
    if (backendDistance && isFinite(backendDistance)) {
      const backendInMeters =
        backendDistance > 100 ? backendDistance : backendDistance * 1000;
      if (calculatedDistance && isFinite(calculatedDistance)) {
        const diff = Math.abs(calculatedDistance - backendInMeters);
        if (diff / calculatedDistance < 0.5) {
          finalDistance = Math.min(calculatedDistance, backendInMeters);
        }
      }
    }
    if (
      backendETA &&
      isFinite(backendETA) &&
      calculatedETA &&
      isFinite(calculatedETA)
    ) {
      const diff = Math.abs(calculatedETA - backendETA);
      if (diff / calculatedETA < 0.5) {
        finalETA = Math.min(calculatedETA, backendETA);
      }
    }
    return {
      distance: isFinite(finalDistance) ? finalDistance / 1000 : null,
      eta: isFinite(finalETA) ? finalETA : null,
    };
  };

  const {
    nextStop,
    stopDetail,
    sourceStop,
    destinationStop,
    distanceFromLastStop,
    speed,
    behindVehicle,
    aheadVehicle,
    destinationDistance,
    destinationETA,
    fallbackDistance,
  } = useMemo(() => {
    try {
      console.log("useMemo triggered with dependencies:", {
        vehicleLocations: vehicleLocations.length,
        stops: data.stops.length,
        routes: data.routes.length,
        stopDetails: Object.keys(stopDetails).length,
        vehicleDirections: Object.keys(vehicleDirections).length,
        visitedStops: visitedStops.size,
        lastVisitedStop,
        effectiveDeviceId,
      });

      // Simplified logic to avoid early returns
      let highlightedVehicle = vehicleLocations.find(
        (v) => String(v.deviceId) === String(effectiveDeviceId)
      ) || { latitude: center.lat, longitude: center.lng }; // Fallback to center

      const currentPosition = {
        lat: parseFloat(highlightedVehicle.latitude),
        lng: parseFloat(highlightedVehicle.longitude),
      };

      const isInbound =
        vehicleDirections[effectiveDeviceId]?.direction === "dest_to_source";
      const currentIndex = findRouteIndex(currentPosition);

      const distanceFromLastStop = lastVisitedStop
        ? calculateImprovedDistance(currentPosition, {
          lat: parseFloat(lastVisitedStop.latitude),
          lng: parseFloat(lastVisitedStop.longitude),
        })
        : Infinity;

      const visibleStops = data.stops.length
        ? data.stops.filter((stop) => {
          if (isInbound) {
            return stop.name !== data.stops[0]?.name;
          } else {
            return stop.name !== data.stops[data.stops.length - 1]?.name;
          }
        })
        : [];

      const stopsWithData = visibleStops
        .filter((stop) => !visitedStops.has(stop.id))
        .map((stop) => ({
          ...stop,
          stopId: stop.id,
          routeIndex: findRouteIndex({
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude),
          }),
          distance: calculateImprovedDistance(currentPosition, {
            lat: parseFloat(stop.latitude),
            lng: parseFloat(stop.longitude),
          }),
        }))
        .filter((stop) => stop.routeIndex !== -1);

      const validStops = stopsWithData
        .filter((stop) =>
          isInbound
            ? stop.routeIndex <= currentIndex
            : stop.routeIndex >= currentIndex
        )
        .sort((a, b) =>
          isInbound ? b.routeIndex - a.routeIndex : a.routeIndex - b.routeIndex
        );

      let behindVehicle = null;
      let aheadVehicle = null;
      const vehiclePositions = vehicleLocations
        .filter((v) => v.deviceId !== effectiveDeviceId)
        .map((v) => ({
          deviceId: v.deviceId,
          routeIndex: findRouteIndex({
            lat: parseFloat(v.latitude),
            lng: parseFloat(v.longitude),
          }),
        }))
        .filter((v) => v.routeIndex !== -1);

      if (vehiclePositions.length) {
        const sortedVehicles = vehiclePositions.sort((a, b) =>
          isInbound ? b.routeIndex - a.routeIndex : a.routeIndex - b.routeIndex
        );
        const currentVehicleIndex = isInbound ? currentIndex : currentIndex;

        const behindCandidates = sortedVehicles.filter((v) =>
          isInbound
            ? v.routeIndex > currentVehicleIndex
            : v.routeIndex < currentVehicleIndex
        );
        if (behindCandidates.length) {
          behindVehicle = behindCandidates[0].deviceId;
        }

        const aheadCandidates = sortedVehicles.filter((v) =>
          isInbound
            ? v.routeIndex < currentVehicleIndex
            : v.routeIndex > currentVehicleIndex
        );
        if (aheadCandidates.length) {
          aheadVehicle = aheadCandidates[0].deviceId;
        }
      }

      const sourceStopDetails = data.stops.length
        ? isInbound
          ? data.stops[data.stops.length - 1]
          : data.stops[0]
        : { name: "Loading...", latitude: center.lat, longitude: center.lng };

      const destinationStopDetails = data.stops.length
        ? isInbound
          ? data.stops[0]
          : data.stops[data.stops.length - 1]
        : { name: "Loading...", latitude: center.lat, longitude: center.lng };

      let nextValidStop = validStops.length ? validStops[0] : null;
      let bestMetrics = { distance: null, eta: null };

      if (nextValidStop) {
        const backendStopDetail = stopDetails[nextValidStop.id] || {};
        const calculatedDistance = nextValidStop.distance || 0;
        const calculatedETA = calculateRealisticETA(calculatedDistance);
        bestMetrics = getBestMetrics(
          calculatedDistance,
          backendStopDetail?.distance,
          calculatedETA,
          backendStopDetail?.eta
        );
      }

      let fallbackDistance = 10;
      if (sourceStopDetails && destinationStopDetails) {
        const sourcePos = {
          lat: parseFloat(sourceStopDetails.latitude),
          lng: parseFloat(sourceStopDetails.longitude),
        };
        const destPos = {
          lat: parseFloat(destinationStopDetails.latitude),
          lng: parseFloat(destinationStopDetails.longitude),
        };
        const distance = calculateImprovedDistance(sourcePos, destPos);
        if (distance !== null && isFinite(distance)) {
          fallbackDistance = distance / 1000;
        }
      }

      let destinationDistance = null;
      let destinationETA = null;
      const targetDestination = isInbound
        ? sourceStopDetails
        : destinationStopDetails;
      if (targetDestination) {
        const destPosition = {
          lat: parseFloat(targetDestination.latitude),
          lng: parseFloat(targetDestination.longitude),
        };
        const calculatedDestDistance = calculateImprovedDistance(
          currentPosition,
          destPosition
        );
        if (
          calculatedDestDistance !== null &&
          isFinite(calculatedDestDistance)
        ) {
          const calculatedDestETA = calculateRealisticETA(
            calculatedDestDistance
          );
          const backendDestDetail = stopDetails[targetDestination.id] || {};
          const destMetrics = getBestMetrics(
            calculatedDestDistance,
            backendDestDetail?.distance,
            calculatedDestETA,
            backendDestDetail?.eta
          );
          destinationDistance = destMetrics.distance;
          destinationETA = destMetrics.eta;
        }
      }

      let rotation = 0;
      if (sourceStopDetails && destinationStopDetails) {
        const sourcePos = {
          latitude: parseFloat(sourceStopDetails.latitude),
          longitude: parseFloat(sourceStopDetails.longitude),
        };
        const destPos = {
          latitude: parseFloat(destinationStopDetails.latitude),
          longitude: parseFloat(destinationStopDetails.longitude),
        };
        rotation = calculateMapRotation(sourcePos, destPos);
        console.log("Map rotation calculated:", rotation, "degrees");
      }

      const currentVehicleSpeed =
        highlightedVehicle?.speed ??
        highlightedVehicle?.Speed ??
        vehicleDirections[effectiveDeviceId]?.speed ??
        0;

      const result = {
        nextStop: nextValidStop,
        stopDetail: {
          ...(stopDetails[nextValidStop?.id] || {}),
          distance: bestMetrics.distance,
          eta: bestMetrics.eta,
        },
        sourceStop: sourceStopDetails,
        destinationStop: destinationStopDetails,
        distanceFromLastStop,
        speed: currentVehicleSpeed,
        behindVehicle,
        aheadVehicle,
        destinationDistance,
        destinationETA,
        fallbackDistance,
        mapRotation: rotation,
      };

      console.log("Stop data calculated:", {
        nextStop: result.nextStop,
        sourceStop: result.sourceStop,
        destinationStop: result.destinationStop,
        speed: result.speed,
        behindVehicle: result.behindVehicle,
        aheadVehicle: result.aheadVehicle,
      });

      return result;
    } catch (error) {
      console.error("Error determining next stop and destination:", error);
      return {
        nextStop: null,
        stopDetail: { distance: null, eta: null },
        sourceStop: { name: "Loading...", latitude: center.lat, longitude: center.lng },
        destinationStop: { name: "Loading...", latitude: center.lat, longitude: center.lng },
        distanceFromLastStop: Infinity,
        speed: 0,
        behindVehicle: null,
        aheadVehicle: null,
        destinationDistance: null,
        destinationETA: null,
        fallbackDistance: 10,
        mapRotation: 0,
      };
    }
  }, [
    vehicleLocations,
    data.stops,
    data.routes,
    stopDetails,
    vehicleDirections,
    visitedStops,
    lastVisitedStop,
    effectiveDeviceId,
  ]);

  // Call onNextStopUpdate whenever stop data changes
  useEffect(() => {
    if (onNextStopUpdate) {
      const dataToSend =
      {
        nextStopName: nextStop?.name || "Loading...",
        distance: stopDetail?.distance || 0,
        eta: stopDetail?.eta ? formatTime(stopDetail.eta) : "N/A",
        sourceStop: sourceStop?.name || "Loading...",
        destinationStop: destinationStop?.name || "Loading...",
        speed: speed || 0,
        behindVehicle: behindVehicle || "N/A",
        aheadVehicle: aheadVehicle || "N/A",
        schedules: data.routes?.[0]?.schedules || [],
        // Add coordinates
        sourceStopCoordinates: sourceStop ? {
          lat: parseFloat(sourceStop.latitude),
          lng: parseFloat(sourceStop.longitude)
        } : null,
        destinationStopCoordinates: destinationStop ? {
          lat: parseFloat(destinationStop.latitude),
          lng: parseFloat(destinationStop.longitude)
        } : null
      };
      console.log("Calling onNextStopUpdate with:", dataToSend);
      onNextStopUpdate(dataToSend);
    }
  }, [
    nextStop,
    stopDetail,
    sourceStop,
    destinationStop,
    speed,
    behindVehicle,
    aheadVehicle,
    onNextStopUpdate,
    data.routes // Add routes to dependencies
  ]);

  useEffect(() => {
    if (map) {
      setMapRotation(0);
      if (typeof map.setHeading === "function") {
        map.setHeading(0);
      }
    }
  }, [map]);

  const lastPannedPositionRef = useRef(null);
  const lastHeadingRef = useRef(0);

  const updateMapOrientation = useCallback(
    (position, mapInstance, headingBearing) => {
      if (!position || !mapInstance) return;

      const lat = parseFloat(position.latitude);
      const lng = parseFloat(position.longitude);
      if (isNaN(lat) || isNaN(lng)) return;

      const bearing =
        typeof headingBearing === "number" && !isNaN(headingBearing)
          ? headingBearing
          : parseFloat(position.bearing ?? position.heading ?? position.Course ?? 0);

      if (!lastPannedPositionRef.current) {
        lastPannedPositionRef.current = { lat, lng };
        mapInstance.panTo({ lat, lng });
      } else {
        const dist = calculateDistance(
          { latitude: lat, longitude: lng },
          { latitude: lastPannedPositionRef.current.lat, longitude: lastPannedPositionRef.current.lng }
        );

        if (dist > 1) {
          lastPannedPositionRef.current = { lat, lng };
          mapInstance.panTo({ lat, lng });
        }
      }

      if (typeof bearing === "number" && !isNaN(bearing)) {
        const diff = Math.abs(((bearing - lastHeadingRef.current + 180) % 360) - 180);
        if (diff > 5 || lastHeadingRef.current === 0) {
          lastHeadingRef.current = bearing;
          setMapRotation(bearing);
          setStopRotation(bearing);
          if (typeof mapInstance.setHeading === "function") {
            mapInstance.setHeading(bearing);
          }
        }
      }
    },
    []
  );

  const handleSSEMessage = useCallback(
    (data) => {
      if (data.type && data.type === "connected") return;

      const deviceId = data.device_id || data.vehicleId;
      const latitude = data.Latitude || data.latitude;
      const longitude = data.Longitude || data.longitude;

      if (deviceId && latitude && longitude) {
        const normalizedData = normalizeDeviceData({
          ...data,
          device_id: deviceId,
          Latitude: latitude,
          Longitude: longitude,
        });

        if (onPositionUpdate && latitude && longitude) {
          onPositionUpdate({
            lat: parseFloat(latitude),
            lng: parseFloat(longitude),
          });
        }

        setVehicleLocations((prev) => {
          const existingIndex = prev.findIndex(
            (item) => String(item.deviceId) === String(normalizedData.deviceId)
          );
          const positionHistory = prev
            .filter((item) => String(item.deviceId) === String(normalizedData.deviceId))
            .slice(-POSITION_HISTORY_SIZE);
          if (existingIndex >= 0) {
            const existing = prev[existingIndex];
            const distance = calculateDistance(
              {
                latitude: normalizedData.latitude,
                longitude: normalizedData.longitude,
              },
              { latitude: existing.latitude, longitude: existing.longitude }
            );
            if (distance < MOVEMENT_THRESHOLD) {
              return prev;
            }
            const newLocations = [...prev];
            newLocations[existingIndex] = normalizedData;
            const { direction, speed, bearing } = determineVehicleDirection(
              normalizedData.deviceId,
              normalizedData,
              data.stops,
              data.routes,
              data.vehicles,
              positionHistory
            );
            setVehicleDirections((prev) => ({
              ...prev,
              [normalizedData.deviceId]: { direction, speed, bearing },
            }));
            if (map && String(normalizedData.deviceId) === String(effectiveDeviceId)) {
              updateMapOrientation(normalizedData, map, bearing);
            }
            return newLocations;
          }
          const newLocations = [...prev, normalizedData];
          const { direction, speed, bearing } = determineVehicleDirection(
            normalizedData.deviceId,
            normalizedData,
            data.stops,
            data.routes,
            data.vehicles,
            positionHistory
          );
          setVehicleDirections((prev) => ({
            ...prev,
            [normalizedData.deviceId]: { direction, speed, bearing },
          }));
          if (map && String(normalizedData.deviceId) === String(effectiveDeviceId)) {
            updateMapOrientation(normalizedData, map, bearing);
          }
          return newLocations;
        });
      } else {
        console.warn("Invalid SSE data:", data);
      }
    },
    [
      data.stops,
      data.routes,
      data.vehicles,
      map,
      updateMapOrientation,
      effectiveDeviceId,
    ]
  );

  useSSE(vehicleIds, config.baseURL, handleSSEMessage);

  // Initial location telemetry fetch & periodic fallback polling
  useEffect(() => {
    if (!effectiveDeviceId) return;

    const fetchLatestLocation = async () => {
      try {
        const response = await axios.get(
          `${config.baseURL}/location/latest/${effectiveDeviceId}`,
          { withCredentials: true }
        );
        if (response.data) {
          const locData = response.data;
          const lat = parseFloat(locData.latitude);
          const lng = parseFloat(locData.longitude);
          const devId = locData.device_id || effectiveDeviceId;

          if (!isNaN(lat) && !isNaN(lng)) {
            const normalizedData = normalizeDeviceData({
              ...locData,
              device_id: devId,
              latitude: lat,
              longitude: lng,
            });

            if (onPositionUpdate) {
              onPositionUpdate({ lat, lng });
            }

            setVehicleLocations((prev) => {
              const existingIndex = prev.findIndex(
                (item) => item.deviceId === normalizedData.deviceId
              );
              const positionHistory = prev
                .filter((item) => item.deviceId === normalizedData.deviceId)
                .slice(-POSITION_HISTORY_SIZE);

              const { direction, speed, bearing } = determineVehicleDirection(
                normalizedData.deviceId,
                normalizedData,
                data.stops || [],
                data.routes || [],
                data.vehicles || [],
                positionHistory
              );

              setVehicleDirections((prevDirs) => ({
                ...prevDirs,
                [normalizedData.deviceId]: { direction, speed, bearing },
              }));

              if (map && String(normalizedData.deviceId) === String(effectiveDeviceId)) {
                updateMapOrientation(normalizedData, map, bearing);
              }

              if (existingIndex >= 0) {
                const newLocations = [...prev];
                newLocations[existingIndex] = normalizedData;
                return newLocations;
              }
              return [...prev, normalizedData];
            });
          }
        }
      } catch (error) {
        console.error("Error fetching latest location telemetry for driver console:", error);
      }
    };

    fetchLatestLocation();
    const intervalId = setInterval(fetchLatestLocation, 15000);
    return () => clearInterval(intervalId);
  }, [
    effectiveDeviceId,
    onPositionUpdate,
    data.stops,
    data.routes,
    data.vehicles,
    map,
    updateMapOrientation,
  ]);

  useEffect(() => {
    if (map && effectiveDeviceId && vehicleLocations.length > 0) {
      const activeVehicle = vehicleLocations.find(
        (v) => String(v.deviceId) === String(effectiveDeviceId)
      );
      if (activeVehicle) {
        const bearing = vehicleDirections[effectiveDeviceId]?.bearing;
        updateMapOrientation(activeVehicle, map, bearing);
      }
    }
  }, [map, effectiveDeviceId, vehicleLocations, vehicleDirections, updateMapOrientation]);

  const onLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);

      // Set initial zoom level
      mapInstance.setZoom(18);
      setZoomLevel(18);

      mapInstance.addListener("zoom_changed", () => {
        const newZoomLevel = mapInstance.getZoom();
        setZoomLevel(newZoomLevel);
      });
    },
    []
  );

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const getBackgroundColor = (deviceId) => {
    const index = Math.abs(hashString(deviceId)) % colors.length;
    return colors[index];
  };

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    return hash;
  };

  const getDirectionArrow = (deviceId) => {
    const direction = vehicleDirections[deviceId]?.direction;
    if (!direction || direction === "unknown") return "";
    return direction === "source_to_dest" ? " ➡️" : " ⬅️";
  };

  const getVehicleIcon = (status, isHighlighted, deviceId) => {
    if (isHighlighted) {
      const direction = vehicleDirections[deviceId]?.direction;
      if (direction === "dest_to_source") {
        return HighlightedBusIconSource;
      } else {
        return HighlightedBusIconDestination;
      }
    }
    switch (status) {
      case "DEAD":
        return InactiveBusIcon;
      case "INACTIVE":
        return InactiveBusIcon;
      case "ACTIVE":
      default:
        return BusIcon;
    }
  };

  const getVehicleDisplayName = (vehicleData, deviceId) => {
    return vehicleData?.name || deviceId || "Unknown Vehicle";
  };

  const stopPositions = useMemo(() => {
    return data.stops.map((stop) => ({
      lat: parseFloat(stop.latitude),
      lng: parseFloat(stop.longitude),
    }));
  }, [data.stops]);

  const vehiclePositions = useMemo(() => {
    return vehicleLocations.map((vehicle) => ({
      lat: parseFloat(vehicle.latitude),
      lng: parseFloat(vehicle.longitude),
    }));
  }, [vehicleLocations]);

  const stopOffsets = useMemo(
    () => calculateLabelOffsets(stopPositions),
    [stopPositions]
  );
  const vehicleOffsets = useMemo(
    () => calculateLabelOffsets(vehiclePositions),
    [vehiclePositions]
  );

  const highlightedVehicleObj = useMemo(
    () => vehicleLocations.find((v) => v.deviceId === effectiveDeviceId),
    [vehicleLocations, effectiveDeviceId]
  );
  const highlightedLat = highlightedVehicleObj?.latitude;
  const highlightedLng = highlightedVehicleObj?.longitude;

  const mapCenter = useMemo(() => {
    const lat = parseFloat(highlightedLat);
    const lng = parseFloat(highlightedLng);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
    return center;
  }, [highlightedLat, highlightedLng]);

  const getStopIcon = (stop, sourceStop, destinationStop, nextStop) => {
    if (stop.id === sourceStop?.id) {
      return {
        type: 'source',
        icon: SourceStopIcon,
        color: '#20A8E0', // Light blue for source
        scale: 8
      };
    } else if (stop.id === destinationStop?.id) {
      return {
        type: 'destination',
        icon: DestinationStopIcon,
        color: '#FF0000', // Red for destination
        scale: 8
      };
    } else if (stop.id === nextStop?.id) {
      return {
        type: 'next',
        icon: NextStopIcon,
        color: '#008C23',
        scale: 6 // Adjust scale as needed
      };
    } else {
      return {
        type: 'regular',
        icon: RegularStopIcon,
        scale: 4
      };
    }
  };


  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return (
      <div style={{ padding: "5px" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div >
        <div>
          {(loadingStates.routes ||
            loadingStates.stops ||
            loadingStates.directions) && (
              <CircularProgress size={20} style={{ marginRight: "10px" }} />
            )}
        </div>
        <MapContainer>
          <MapWrapper>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              onLoad={onLoad}
              onUnmount={onUnmount}
              center={mapCenter}
              zoom={18}
              options={{
                heading: mapRotation,
                mapId: "a7388450e4f50eab65a0b0ff",
                mapTypeId: "terrain",
                tilt: 0,
                mapTypeControl: true,
                streetViewControl: true,
                rotateControl: false,
                fullscreenControl: false,
                scrollwheel: true,
                gestureHandling: "cooperative",
                minZoom: MAP_ORIENTATION.MIN_ZOOM || 10,
                maxZoom: MAP_ORIENTATION.MAX_ZOOM || 20,
                disableDefaultUI: false,
                zoomControl: true,
              }}
            >
              {isLoaded && !loadingStates.stops && data.stops.length > 0 ? (
                data.stops.map((stop, index) => {
                  const lat = parseFloat(stop.latitude);
                  const lng = parseFloat(stop.longitude);
                  if (isNaN(lat) || isNaN(lng)) {
                    console.warn("Invalid stop coordinates:", stop);
                    return null;
                  }
                  const stopConfig = getStopIcon(stop, sourceStop, destinationStop, nextStop);
                  const isSourceOrDestinationOrNext =
                    stopConfig.type === 'source' ||
                    stopConfig.type === 'destination' ||
                    stopConfig.type === 'next';
                  return (
                    <React.Fragment key={stop.id}>
                      <OverlayView
                        position={{ lat, lng }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div
                          style={{
                            position: "relative",
                            top: `calc(-90px - ${zoomOffset}px - ${stopOffsets[index] || 0
                              }px)`,
                            left: "0px",
                            width: "2px",
                            height: "2px",
                          }}
                          data-zoom-offset={zoomOffset}
                        >
                          {/* <StopLine
                            zoomoffset={zoomOffset}
                            offset={stopOffsets[index]}
                            alternate={index % 2 === 1}
                            rotation={mapRotation}
                          /> */}
                          <StopLabel
                            alternate={index % 2 === 1}
                            zoomoffset={zoomOffset}
                            offset={stopOffsets[index]}
                            rotation={stopRotation}
                            style={{
                              fontWeight: isSourceOrDestinationOrNext ? 'bold' : 'normal',
                              color: isSourceOrDestinationOrNext ? "#FFFFFF" : "#000000",
                              backgroundColor: isSourceOrDestinationOrNext ? stopConfig.color : undefined,
                              border: isSourceOrDestinationOrNext ? `1px solid ${stopConfig.color}` : undefined
                            }}
                          >
                            {stop.name}
                          </StopLabel>
                        </div>
                      </OverlayView>

                      <OverlayView
                        position={{ lat, lng }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div
                          style={{
                            position: "relative",
                            width: `${stopConfig.scale * 4}px`,
                            height: `${stopConfig.scale * 4}px`,
                            transform: "translate(-50%, -50%)",
                            backgroundImage: `url(${stopConfig.icon})`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center",
                            zIndex: isSourceOrDestinationOrNext ? 1000 : 100
                          }}
                          title={`${stopConfig.type.toUpperCase()}: ${stop.name}`}
                        />
                      </OverlayView>

                    </React.Fragment>
                  );
                })
              ) : (
                <></>
              )}

              {isLoaded && vehicleLocations.length > 0 ? (
                vehicleLocations.map((vehicle, index) => {
                  const vehicleData =
                    data.vehicles.find(
                      (v) => v.device_id === vehicle.deviceId
                    ) || {};
                  const vehicleDisplayName = getVehicleDisplayName(
                    vehicleData,
                    vehicle.deviceId
                  );
                  const isHighlighted = String(vehicle.deviceId) === String(effectiveDeviceId);
                  const vehicleIcon = getVehicleIcon(
                    vehicleData.status,
                    isHighlighted,
                    vehicle.deviceId
                  );
                  const lat = parseFloat(vehicle.latitude);
                  const lng = parseFloat(vehicle.longitude);
                  if (isNaN(lat) || isNaN(lng)) {
                    console.warn("Invalid vehicle coordinates:", vehicle);
                    return null;
                  }
                  let vehicleBearing = vehicleDirections[vehicle.deviceId]?.bearing;
                  if (
                    typeof vehicleBearing !== "number" ||
                    isNaN(vehicleBearing) ||
                    vehicleBearing === 0
                  ) {
                    const routeCoords = data.routes?.[0]?.routeCoordinates;
                    if (routeCoords && routeCoords.length >= 2) {
                      let minDistance = Infinity;
                      let closestIndex = 0;
                      for (let i = 0; i < routeCoords.length; i++) {
                        const dist = calculateDistance(
                          { latitude: lat, longitude: lng },
                          {
                            latitude: parseFloat(routeCoords[i].latitude),
                            longitude: parseFloat(routeCoords[i].longitude),
                          }
                        );
                        if (dist < minDistance) {
                          minDistance = dist;
                          closestIndex = i;
                        }
                      }
                      const isInbound =
                        vehicleDirections[vehicle.deviceId]?.direction === "dest_to_source";
                      let p1, p2;
                      if (isInbound) {
                        p1 = routeCoords[closestIndex];
                        p2 = routeCoords[Math.max(0, closestIndex - 1)];
                        if (closestIndex === 0 && routeCoords.length > 1) {
                          p1 = routeCoords[1];
                          p2 = routeCoords[0];
                        }
                      } else {
                        p1 = routeCoords[closestIndex];
                        p2 = routeCoords[Math.min(closestIndex + 1, routeCoords.length - 1)];
                        if (closestIndex === routeCoords.length - 1 && routeCoords.length > 1) {
                          p1 = routeCoords[routeCoords.length - 2];
                          p2 = routeCoords[routeCoords.length - 1];
                        }
                      }
                      if (p1 && p2 && (p1.latitude !== p2.latitude || p1.longitude !== p2.longitude)) {
                        vehicleBearing = calculateBearing(
                          { latitude: parseFloat(p1.latitude), longitude: parseFloat(p1.longitude) },
                          { latitude: parseFloat(p2.latitude), longitude: parseFloat(p2.longitude) }
                        );
                      }
                    }
                  }
                  if (typeof vehicleBearing !== "number" || isNaN(vehicleBearing)) {
                    vehicleBearing = 0;
                  }

                  const currentDir = vehicleDirections[vehicle.deviceId]?.direction;
                  const routeCoords = data.routes?.[0]?.routeCoordinates;
                  if (routeCoords && routeCoords.length >= 2) {
                    let minDistance = Infinity;
                    let closestIndex = 0;
                    for (let i = 0; i < routeCoords.length; i++) {
                      const dist = calculateDistance(
                        { latitude: lat, longitude: lng },
                        {
                          latitude: parseFloat(routeCoords[i].latitude),
                          longitude: parseFloat(routeCoords[i].longitude),
                        }
                      );
                      if (dist < minDistance) {
                        minDistance = dist;
                        closestIndex = i;
                      }
                    }
                    let p1 = routeCoords[closestIndex];
                    let p2 = routeCoords[Math.min(closestIndex + 1, routeCoords.length - 1)];
                    if (closestIndex === routeCoords.length - 1 && routeCoords.length > 1) {
                      p1 = routeCoords[closestIndex - 1];
                      p2 = routeCoords[closestIndex];
                    }
                    if (p1 && p2 && (p1.latitude !== p2.latitude || p1.longitude !== p2.longitude)) {
                      const segBearing = calculateBearing(
                        { latitude: parseFloat(p1.latitude), longitude: parseFloat(p1.longitude) },
                        { latitude: parseFloat(p2.latitude), longitude: parseFloat(p2.longitude) }
                      );
                      const expectedBearing = currentDir === "dest_to_source" ? (segBearing + 180) % 360 : segBearing;
                      if (vehicleBearing === 0) {
                        vehicleBearing = expectedBearing;
                      } else {
                        const diff = Math.abs(((vehicleBearing - expectedBearing + 180) % 360) - 180);
                        if (diff > 90) {
                          vehicleBearing = (vehicleBearing + 180) % 360;
                        }
                      }
                    }
                  }

                  const adjustedRotation = vehicleBearing - mapRotation;

                  return (
                    <React.Fragment key={vehicle.deviceId}>
                      {!isHighlighted &&
                        <OverlayView
                          position={{ lat, lng }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <div
                            style={{
                              position: "relative",
                              top: `${-120 - zoomOffset - (vehicleOffsets[index] || 0)
                                }px`,
                              left: "0px",
                              width: "2px",
                              height: "2px",
                            }}
                          >
                            <VehicleLine
                              sx={{
                                backgroundColor: getBackgroundColor(
                                  vehicle.deviceId
                                ),
                              }}
                              offset={vehicleOffsets[index]}
                            />
                            <VehicleLabel
                              sx={{
                                backgroundColor: getBackgroundColor(
                                  vehicle.deviceId
                                ),
                              }}
                              zoomoffset={zoomOffset}
                              offset={vehicleOffsets[index]}
                              rotation={stopRotation}
                              isHighlighted={isHighlighted}
                            >
                              {vehicleDisplayName}
                              <span style={{ fontWeight: "bold" }}>
                                {getDirectionArrow(vehicle.deviceId)}
                              </span>
                            </VehicleLabel>
                          </div>
                        </OverlayView>
                      }
                      <OverlayView
                        position={{
                          lat: parseFloat(vehicle.latitude),
                          lng: parseFloat(vehicle.longitude),
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div style={{ position: "relative", width: 0, height: 0 }}>
                          {isHighlighted && (
                            <>
                              <AnimatedCircle /> {/* Inner circle */}
                              <AnimatedCircle className="outer" /> {/* Outer circle */}
                            </>
                          )}
                          {isHighlighted && isOverSpeeding && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "0px",
                                  left: "0px",
                                  transform: "translate(-50%, -50%)",
                                  backgroundColor: "#FF0000",
                                  color: "#ffffff",
                                  borderRadius: "50%",
                                  width: "22px",
                                  height: "22px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: "bold",
                                  fontSize: "13px",
                                  border: "2px solid #ffffff",
                                  zIndex: 1000,
                                  boxShadow: "0 2px 6px rgba(255, 0, 0, 0.5)",
                                }}
                                title="Overspeed Alert"
                              >
                                ⚠
                              </div>
                            )}
                          <VehicleIconContainer
                            style={{
                              backgroundImage: `url(${vehicleIcon})`,
                              transform: `translate(-50%, -50%) rotate(${adjustedRotation}deg)`,
                            }}
                          />
                        </div>
                      </OverlayView>
                    </React.Fragment>
                  );
                })
              ) : (
                <></>
              )}

              {isLoaded &&
                !loadingStates.directions &&
                directions.length > 0 &&
                directions.map((direction, index) => (
                  <DirectionsRenderer
                    key={index}
                    directions={direction}
                    options={{
                      suppressMarkers: true,
                      preserveViewport: true,
                      polylineOptions: {
                        strokeColor: direction.routeColor || "#000000",
                        strokeOpacity: 0.8,
                        strokeWeight: 4,
                      },
                    }}
                  />
                ))}
            </GoogleMap>
          </MapWrapper>
        </MapContainer>
      </div>

      {isLoaded && (
        <InfoBox rotation={mapRotation}>
          <InfoText>
            {destinationETA !== null && isFinite(destinationETA)
              ? formatTime(destinationETA)
              : formatTime(
                calculateRealisticETA((fallbackDistance || 10) * 1000)
              )}
          </InfoText>
          <SubInfoText>
            {destinationDistance !== null && isFinite(destinationDistance)
              ? `${destinationDistance.toFixed(1)} km`
              : `${(fallbackDistance || 10).toFixed(1)} km`}{" "}
            •{" "}
            {destinationETA !== null && isFinite(destinationETA)
              ? new Date(Date.now() + destinationETA * 1000)
                .toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })
                .toLowerCase()
              : new Date(
                Date.now() +
                calculateRealisticETA((fallbackDistance || 10) * 1000) *
                1000
              )
                .toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })
                .toLowerCase()}
          </SubInfoText>
          {/* <SubInfoText>
            Source: {sourceStop?.name || "Loading..."} | Destination:{" "}
            {destinationStop?.name || "Loading..."} | Next Stop:{" "}
            {nextStop?.name || "Loading..."}
          </SubInfoText> */}
        </InfoBox>
      )}
    </ThemeProvider>
  );
}

export default React.memo(DriverSpecificMap);
